import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "@/components/LocalLink";
import { IMAGES } from "@/data/catalog";
import { CONTACT_EMAIL } from "@/data/policies";
import { PageShell, usePageMeta, useJsonLd } from "@/components/PageShell";
import { MeasureCalc } from "@/components/MeasureCalc";
import { useLang } from "@/i18n/LanguageContext";

const TOPICS = [["How to use", "The measure by weight, and how long a jar lasts.", "#how-to-use"], ["Shipping", "Where we ship, how long it takes, what it costs.", "#shipping"], ["Returns", "Unopened jars, damaged parcels, refunds.", "#shipping"], ["Standing Orders", "Coming soon: timed refills, skip or pause any time.", "#standing-orders"]];
const MEASURE = [["Up to 25 lb", "¼ measure", "about 240 days"], ["26–50 lb", "½ measure", "about 120 days"], ["51–90 lb", "¾ measure", "about 80 days"], ["Over 90 lb", "1 measure", "about 60 days"]];
const FACTS = [["Where we ship", "Canada only, for now"], ["Packed", "2–4 business days"], ["Delivery", "3–8 business days"], ["Shipping", "$15 flat · free over $125 before savings"], ["Returns", "Unopened jars, 30 days"], ["Arrived damaged?", "Photo within 14 days, free replacement"]];

const Faq = () => {
  const { lang } = useLang();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(0);
  useEffect(() => { axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/faq`).then((r) => setD(r.data)).catch(() => setD({ items: [] })); }, []);
  const items = (Array.isArray(d?.items) ? d.items : []).map((i) => (lang === "fr" ? i.fr : i.en)).filter((i) => i?.q);
  useJsonLd("faq-schema", items.length ? { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: items.map((i) => ({ "@type": "Question", name: i.q, acceptedAnswer: { "@type": "Answer", text: i.a } })) } : null);
  if (!items.length) return <p className="muted" data-testid="faq-empty">{d ? "Answers are on their way. Ask the Apothecary in the meantime." : "…"}</p>;
  return (
    <div data-testid="faq-list" data-no-translate>
      {items.map((i, n) => (
        <div key={i.q} className={`qa${open === n ? " open" : ""}`} data-testid={`faq-item-${n}`} role="button" tabIndex={0} aria-expanded={open === n}
          onClick={() => setOpen(open === n ? -1 : n)} onKeyDown={(e) => e.key === "Enter" && setOpen(open === n ? -1 : n)} style={{ cursor: "pointer" }}>
          {open === n ? <><b style={{ fontWeight: 500 }} data-testid={`faq-q-${n}`}>{i.q}</b><p data-testid={`faq-a-${n}`}>{i.a}</p></> : <><span data-testid={`faq-q-${n}`}>{i.q}</span><span>+</span></>}
        </div>
      ))}
    </div>
  );
};

export default function Help() {
  usePageMeta("Help · Dirty Paws Apothecary", "How to use each jar, shipping and returns, Standing Orders, and answers to the questions we hear most.");
  const openChat = () => document.querySelector('[data-testid="chat-open"]')?.click();
  return (
    <PageShell testid="help-page" page="help">
      <div className="wrap">
        <div className="page-head"><div className="eyebrow">Help</div><h1>Questions, answered plainly.</h1><p>How much to give, how long a jar lasts, shipping and returns. Or ask the Apothecary, day and night.</p></div>
        <div className="topics" data-testid="help-topics">
          {TOPICS.map(([h, p, href], i) => <a key={h} href={href} className="panel topic" data-testid={`help-topic-${h.toLowerCase().replace(/\s+/g, "-")}`}><div className="eyebrow">0{i + 1}</div><h4>{h}</h4><p>{p}</p></a>)}
        </div>
        <div className="how" id="how-to-use">
          <div><div className="eyebrow">How to use</div><h2 className="sec">One measure a day, by weight</h2>
            <table data-testid="measure-table"><tbody><tr><th>Dog's weight</th><th>Daily measure</th><th>One jar lasts</th></tr>
              {MEASURE.map(([w, m, d]) => <tr key={w}><td>{w}</td><td><b>{m}</b></td><td>{d}</td></tr>)}</tbody></table>
            <p className="muted" style={{ fontSize: 13.5, marginTop: 14 }}>Sprinkle it on their food or mix it in, whatever your dog prefers. Puppies under a year: ask your vet first.</p>
            <div style={{ marginTop: 16 }}><MeasureCalc testid="help-measure" /></div>
          </div>
          <img src={IMAGES.measure} alt="A measure of mushroom powder on the apothecary bench" />
        </div>
        <div className="cols2">
          <div id="shipping"><div className="eyebrow">Shipping &amp; returns</div><h2 className="sec">The short version</h2>
            <ul className="facts" data-testid="shipping-list">{FACTS.map(([k, v]) => <li key={k}>{k}<b>{v}</b></li>)}</ul>
            <Link to="/policies/shipping-returns" className="btn-text" style={{ display: "inline-block", marginTop: 18 }} data-testid="help-full-policy">Full shipping &amp; returns policy</Link>
            <div id="standing-orders" style={{ marginTop: 34 }}><div className="eyebrow">Standing Orders</div>
              <p className="muted" style={{ marginTop: 8 }} data-testid="help-standing">Coming soon, not open yet. When they open, you'll be able to skip, pause or cancel from your account.</p></div>
          </div>
          <div id="questions"><div className="eyebrow">Common questions</div><h2 className="sec">Asked most</h2><Faq /></div>
        </div>
        <div className="panel contact" data-testid="still-stuck">
          <div><h2 className="sec" style={{ margin: "0 0 4px" }}>Still stuck?</h2><p className="muted">Write to {CONTACT_EMAIL}. A human (supervised by a dog) will reply.</p></div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button type="button" className="btn btn-primary" onClick={openChat} data-testid="faq-ask-button">Ask the Apothecary</button>
            <a href={`mailto:${CONTACT_EMAIL}`} className="btn btn-ghost" data-testid="help-write">Write to us</a>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
